import React, { useEffect, useLayoutEffect, useRef } from "react";
import type {
  ConsultNoteImagingRow,
  ConsultNoteFormAssessmentSection,
  ConsultNoteFormPhysicalExamSection,
  ConsultNoteFormTextSection,
  ConsultNotePendingAction,
  ConsultNoteSignedAddendum,
  ConsultNoteVisit,
} from "./types";
import { SectionCardHeader, SourceTable } from "./primitives";

const MIN_TEXTAREA_ROWS = 4;
const DEFAULT_LINE_HEIGHT_PX = 24;

function pixelValue(value: string) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function resizeTextarea(textarea: HTMLTextAreaElement) {
  const styles = window.getComputedStyle(textarea);
  const lineHeight = pixelValue(styles.lineHeight) || DEFAULT_LINE_HEIGHT_PX;
  const verticalPadding = pixelValue(styles.paddingTop) + pixelValue(styles.paddingBottom);
  const verticalBorder = pixelValue(styles.borderTopWidth) + pixelValue(styles.borderBottomWidth);
  const minimumHeight = (lineHeight * MIN_TEXTAREA_ROWS) + verticalPadding + verticalBorder;

  textarea.style.height = "0px";
  textarea.style.height = `${Math.max(textarea.scrollHeight + verticalBorder, minimumHeight)}px`;
}

function AutoSizingTextarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useLayoutEffect(() => {
    if (!textareaRef.current) return;

    resizeTextarea(textareaRef.current);
  }, [props.value]);

  useEffect(() => {
    if (!textareaRef.current) return undefined;

    const handleResize = () => {
      if (!textareaRef.current) return;

      resizeTextarea(textareaRef.current);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <textarea
      {...props}
      ref={textareaRef}
      rows={MIN_TEXTAREA_ROWS}
      className={[props.className, "overflow-hidden resize-none"].filter(Boolean).join(" ")}
    />
  );
}

function ImagingRowsTable({ imagingRows }: { imagingRows: ConsultNoteImagingRow[] }) {
  if (imagingRows.length === 0) return null;

  return (
    <div className="mt-4 overflow-hidden rounded-xl border border-neutral-200/80 dark:border-neutral-800">
      <div className="border-b border-neutral-200/80 bg-neutral-50/80 px-3 py-2 dark:border-neutral-800 dark:bg-neutral-900/50">
        <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">Imaging</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
          <thead className="bg-neutral-100/90 dark:bg-neutral-900/80">
            <tr>
              <th className="px-3 py-2 text-left text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
                Date
              </th>
              <th className="px-3 py-2 text-left text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
                Study
              </th>
              <th className="px-3 py-2 text-left text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
                Result
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
            {imagingRows.map((row) => (
              <tr key={`${row.date}-${row.imageType}-${row.results}`}>
                <td className="whitespace-nowrap px-3 py-2 text-sm">
                  {row.openUrl ? (
                    <a
                      href={row.openUrl}
                      className="font-medium text-emerald-700 hover:text-emerald-800 dark:text-emerald-300 dark:hover:text-emerald-200"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {row.date || "Undated"}
                    </a>
                  ) : (
                    <span className="text-neutral-700 dark:text-neutral-200">{row.date || "Undated"}</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-sm font-medium text-neutral-900 dark:text-white">{row.imageType || "Imaging"}</td>
                <td className="px-3 py-2 text-sm text-neutral-700 dark:text-neutral-200">{row.results || "No result text"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface TextSectionCardProps {
  section: ConsultNoteFormTextSection;
  readOnly: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
  headerAction?: React.ReactNode;
}

export function TextSectionCard({ section, readOnly, disabled, onChange, headerAction }: TextSectionCardProps) {
  if (section.key === "medical_necessity") {
    return (
      <section className="app-surface p-4">
        <div className="flex items-center gap-3">
          <label htmlFor="medical-necessity" className="font-semibold text-neutral-900 dark:text-white">Medical Necessity</label>
          <span className="text-xs text-neutral-500">Optional</span>
        </div>
        <p id="medical-necessity-description" className="mt-2 text-sm text-neutral-500">
          Review or enter patient-specific wording for this consultation note.
        </p>
        <AutoSizingTextarea
          id="medical-necessity"
          aria-describedby="medical-necessity-description medical-necessity-help"
          value={section.value}
          onChange={(event) => onChange(event.target.value)}
          className="app-input mt-4 min-h-28 text-sm leading-6"
          readOnly={readOnly}
          disabled={disabled}
        />
        <p id="medical-necessity-help" className="mt-2 text-xs text-neutral-500">
          Included in a separate Medical Necessity section in the PDF. Leave blank to omit.
        </p>
      </section>
    );
  }

  return (
    <section className="app-surface p-4">
      <SectionCardHeader title={section.title} action={headerAction} />

      <div className="mt-4">
        <AutoSizingTextarea
          value={section.value}
          onChange={(event) => onChange(event.target.value)}
          className="app-input min-h-28 font-mono text-sm leading-6"
          readOnly={readOnly}
          disabled={disabled}
        />
      </div>

      <ImagingRowsTable imagingRows={section.imagingRows} />

      <SourceTable sourceRefs={section.sourceRefs} />
    </section>
  );
}

interface VisitDetailsCardProps {
  visit: ConsultNoteVisit;
  readOnly: boolean;
  disabled: boolean;
  onDateChange: (value: string) => void;
  onTimeChange: (value: string) => void;
}

export function VisitDetailsCard({ visit, readOnly, disabled, onDateChange, onTimeChange }: VisitDetailsCardProps) {
  return (
    <section className="app-surface p-4">
      <div>
        <h2 className="text-lg font-semibold text-neutral-950 dark:text-white">Date of Visit</h2>
      </div>

      <div className="mt-4 flex flex-nowrap items-end gap-3">
        <div className="min-w-0 flex-1">
          <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Date</label>
          <input
            type="date"
            value={visit.date}
            onChange={(event) => onDateChange(event.target.value)}
            className="app-input"
            readOnly={readOnly}
            disabled={disabled}
            required
          />
        </div>
        <div className="min-w-0 flex-1">
          <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Time</label>
          <input
            type="time"
            value={visit.time}
            onChange={(event) => onTimeChange(event.target.value)}
            className="app-input"
            readOnly={readOnly}
            disabled={disabled}
            step={60}
          />
        </div>
      </div>

      <p className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
        Time is optional. If blank, the PDF shows the visit date without a time.
      </p>
    </section>
  );
}

interface PhysicalExamSectionCardProps {
  section: ConsultNoteFormPhysicalExamSection;
  readOnly: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
  onAddExam: () => void;
}

export function PhysicalExamSectionCard({
  section,
  readOnly,
  disabled,
  onChange,
  onAddExam,
}: PhysicalExamSectionCardProps) {
  return (
    <section className="app-surface p-4">
      <SectionCardHeader
        title={section.title}
        action={(
          <button
            type="button"
            className="app-button-secondary"
            onClick={onAddExam}
            disabled={readOnly || disabled}
          >
            Add exam
          </button>
        )}
      />

      <div className="mt-4">
        <AutoSizingTextarea
          value={section.value}
          onChange={(event) => onChange(event.target.value)}
          className="app-input min-h-40 font-mono text-sm leading-6"
          readOnly={readOnly}
          disabled={disabled}
        />
      </div>

      <SourceTable sourceRefs={section.sourceRefs} />
    </section>
  );
}

interface AssessmentSectionCardProps {
  section: ConsultNoteFormAssessmentSection;
  readOnly: boolean;
  disabled: boolean;
  onToggle: (optionKey: string, checked: boolean) => void;
  onCustomTextChange: (value: string) => void;
}

export function AssessmentSectionCard({ section, readOnly, disabled, onToggle, onCustomTextChange }: AssessmentSectionCardProps) {
  const customSelected = section.options.some((option) => option.key === "custom" && option.selected);

  return (
    <section className="app-surface p-4">
      <SectionCardHeader title={section.title} />

      <div className="mt-4 grid gap-2 md:grid-cols-2">
        {section.options.map((option) => (
          <label
            key={option.key}
            className="flex items-center gap-3 rounded-lg border border-neutral-200 px-3 py-2.5 dark:border-neutral-800"
          >
            <input
              type="checkbox"
              checked={option.selected}
              onChange={(event) => onToggle(option.key, event.target.checked)}
              className="h-4 w-4 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-500"
              disabled={readOnly || disabled}
            />
            <span className="font-medium text-neutral-950 dark:text-white">{option.label}</span>
          </label>
        ))}
      </div>

      {customSelected ? (
        <div className="mt-4">
          <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Custom assessment</label>
          <AutoSizingTextarea
            value={section.customText}
            onChange={(event) => onCustomTextChange(event.target.value)}
            className="app-input mt-2 min-h-28 font-mono text-sm leading-6"
            readOnly={readOnly}
            disabled={disabled}
            placeholder="Enter custom assessment text"
          />
        </div>
      ) : null}

      <SourceTable sourceRefs={section.sourceRefs} />
    </section>
  );
}

interface AddendumSectionCardProps {
  signedAddendums: ConsultNoteSignedAddendum[];
  draftBody: string;
  canSign: boolean;
  readOnly: boolean;
  disabled: boolean;
  pendingAction: ConsultNotePendingAction | null;
  onDraftBodyChange: (value: string) => void;
  onSaveDraft: () => void;
  onSign: () => void;
}

export function AddendumSectionCard({
  signedAddendums,
  draftBody,
  canSign,
  readOnly,
  disabled,
  pendingAction,
  onDraftBodyChange,
  onSaveDraft,
  onSign,
}: AddendumSectionCardProps) {
  return (
    <section className="app-surface p-4">
      <SectionCardHeader title="Addendum" />

      {signedAddendums.length > 0 ? (
        <div className="mt-4 space-y-3">
          {signedAddendums.map((addendum) => (
            <article key={addendum.id} className="rounded-lg border border-neutral-200/80 p-3 dark:border-neutral-800">
              <p className="whitespace-pre-wrap font-mono text-sm leading-6 text-neutral-800 dark:text-neutral-100">{addendum.body}</p>
              <p className="mt-3 text-xs font-medium uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
                Addended by {addendum.signedByName} on {addendum.signedAtLabel}
              </p>
            </article>
          ))}
        </div>
      ) : null}

      {!readOnly ? (
        <div className="mt-4">
          <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Draft addendum</label>
          <AutoSizingTextarea
            value={draftBody}
            onChange={(event) => onDraftBodyChange(event.target.value)}
            className="app-input mt-2 min-h-32 font-mono text-sm leading-6"
            readOnly={readOnly}
            disabled={disabled}
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" className="app-button-secondary" onClick={onSaveDraft} disabled={disabled}>
              {pendingAction === "saveAddendum" ? "Saving..." : "Save Addendum"}
            </button>
            {canSign ? (
              <button type="button" className="app-button-primary" onClick={onSign} disabled={disabled}>
                {pendingAction === "signAddendum" ? "Signing..." : "Sign Addendum"}
              </button>
            ) : null}
          </div>
          <div className="mt-2 space-y-1 text-xs leading-5 text-neutral-500 dark:text-neutral-400">
            <p>Save Addendum keeps this as an unsigned draft and does not update the PDF or Tebra.</p>
            {canSign ? (
              <p>Sign Addendum locks this text, adds a new addendum timestamp, regenerates the PDF, and queues Tebra export.</p>
            ) : (
              <p>A surgeon must sign the addendum before it appears in the PDF or is queued for Tebra export.</p>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}
